import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { isValidDateKey, todayKey } from "@/domain/dates";
import type { Entry, Scenario, Subscription } from "@/domain/entities";
import { ScenarioRunner } from "@/domain/scenario/ScenarioRunner";
import { Button } from "../components/Button";
import { PencilIcon } from "../components/PencilIcon";
import { Screen } from "../components/Screen";
import { SwipeRow } from "../components/SwipeRow";
import { useApp } from "../container";
import { formatAnswer, formatDate, formatEpisode } from "../format";
import { useQuery } from "../hooks/useQuery";
import { useLocale } from "../i18n/LocaleProvider";
import { format } from "../i18n/strings";
import styles from "./DayScreen.module.css";

interface Row {
  subscription: Subscription;
  scenario: Scenario;
  entries: Entry[];
}

export function DayScreen() {
  const { date = "" } = useParams();
  const app = useApp();
  const navigate = useNavigate();
  const { t, pick, locale } = useLocale();
  const today = todayKey(app.clock);
  const valid = isValidDateKey(date);
  const future = valid && date > today;
  const [pendingDelete, setPendingDelete] = useState<Entry | null>(null);

  const rows = useQuery<Row[]>(async () => {
    if (!valid) return [];
    const [subscriptions, scenarios] = await Promise.all([app.store.subscriptions.listAll(), app.store.scenarios.list()]);
    const result: Row[] = [];
    // Any past day can be filled in, including days before the scenario was added.
    for (const subscription of subscriptions) {
      const scenario = scenarios.find((s) => s.id === subscription.scenarioId);
      if (!scenario) continue;
      result.push({ subscription, scenario, entries: await app.store.entries.listByDate(subscription.id, date) });
    }
    return result;
  }, [date], app.changeSources);

  const addEpisode = async ({ subscription, scenario }: Row) => {
    const entry = await app.entries.createEpisode(subscription, scenario, date);
    navigate(`/answer/${subscription.id}/${date}/${entry.id}`);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    app.analytics.track("entry_deleted");
    await app.entries.remove(pendingDelete);
    setPendingDelete(null);
  };

  const renderEntry = (row: Row, entry: Entry, index: number) => {
    if (pendingDelete?.id === entry.id) {
      return (
        <div key={entry.id} className={styles.confirm} role="alertdialog" aria-label={t.questionnaire.deleteEntry}>
          <p>{t.questionnaire.deleteConfirm}</p>
          <div className={styles.actions}>
            <Button variant="ghost" onClick={() => setPendingDelete(null)}>{t.questionnaire.deleteNo}</Button>
            <Button variant="danger" onClick={confirmDelete}>{t.questionnaire.deleteYes}</Button>
          </div>
        </div>
      );
    }
    const visible = new ScenarioRunner(row.scenario).visibleQuestions(entry.answers);
    const link = `/answer/${row.subscription.id}/${date}/${entry.id}`;
    const summary = formatEpisode(entry, row.scenario, locale);
    return (
      <SwipeRow key={entry.id} onDelete={() => setPendingDelete(entry)} deleteLabel={t.questionnaire.deleteEntry}>
      <div className={styles.entry}>
        <Link to={link} className={styles.entryLink}>
          <span className={styles.summary}>
            {format(t.today.entryNumber, { n: index + 1 })}
            {summary && ` · ${summary}`}
          </span>
          <PencilIcon className={styles.editIcon} />
        </Link>
        {entry.status === "skipped" && <span className={styles.muted}>{t.day.skippedDay}</span>}
        {entry.status !== "skipped" && (
          <div className={styles.answers}>
            {visible.map((question) => (
              <div key={question.id} className={styles.answer}>
                <span className={styles.question}>{pick(question.text)}</span>
                <span>{formatAnswer(question, entry.answers[question.id], locale, t)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      </SwipeRow>
    );
  };

  return (
    <Screen title={valid ? formatDate(date, locale) : t.day.noEntry} back={() => navigate(-1)}>
      {future && <p className={styles.muted}>{t.day.future}</p>}
      <ul className={styles.list}>
        {rows.data?.map((row) => {
          const episodic = row.scenario.episodes !== undefined;
          const shown = episodic ? row.entries : row.entries.slice(0, 1);
          return (
            <li key={row.subscription.id} className={styles.card}>
              <span className={styles.name}>{pick(row.scenario.name)}</span>
              {shown.length === 0 && <span className={styles.muted}>{t.day.noEntry}</span>}
              {shown.map((entry, index) => renderEntry(row, entry, index))}
              {!future && shown.length === 0 && (
                <Link to={`/answer/${row.subscription.id}/${date}`}>
                  <Button tabIndex={-1}>{t.day.fillIn}</Button>
                </Link>
              )}
              {!future && episodic && shown.some((e) => e.status !== "skipped") && (
                <Button variant="ghost" onClick={() => addEpisode(row)}>{t.today.addEpisode}</Button>
              )}
            </li>
          );
        })}
      </ul>
    </Screen>
  );
}
